const WORKOUT_KINDS = new Set(['member_workout_plan_adjustment', 'dojo_workout_plan_adjustment']);

const text = value => String(value ?? '').trim();
const key = value => text(value).toLowerCase();

function formatCurrentPrescription(exercise) {
  const sets = Array.isArray(exercise?.sets) ? exercise.sets : [];
  const reps = [...new Set(sets.map(set => set?.repetitions ?? set?.reps).filter(value => value != null))];
  if (sets.length === 0 && reps.length === 0) return '';
  return `${sets.length || '?'} × ${reps.length === 1 ? reps[0] : (reps.join('/') || '?')}`;
}

function formatProposedPrescription(exercise) {
  const sets = exercise?.sets ?? exercise?.set_count;
  const reps = exercise?.reps ?? exercise?.rep_range;
  return sets || reps ? `${sets || '?'} × ${reps || '?'}` : '';
}

function affectedProposalCount(proposal) {
  const affected = new Set();
  for (const change of proposal?.proposed_changes || []) {
    const entryId = text(change?.entry_id);
    const removals = Array.isArray(change?.removed_exercises) ? change.removed_exercises : [];
    const replacementIds = new Set(removals.map(removal => key(removal?.replacement_exercise_id)).filter(Boolean));
    for (const exercise of change?.exercises || []) {
      if (exercise?.proposal_status !== 'adjusted' || replacementIds.has(key(exercise?.exercise_id))) continue;
      affected.add(`${entryId}:${key(exercise?.exercise_id)}`);
    }
    for (const removal of removals) {
      if (removal?.exercise_id) affected.add(`${entryId}:${key(removal.exercise_id)}`);
    }
  }
  return affected.size;
}

export function countWorkoutProposalAnnotations(proposal) {
  return affectedProposalCount(proposal);
}

export function buildWorkoutProposalAnnotations({ workout, proposals = [] } = {}) {
  if (!workout || workout.is_template || workout.readOnly) return {};
  const entryId = text(workout?.source?.activity_plan_entry_id);
  if (!entryId) return {};
  const currentExercises = Array.isArray(workout?.data?.exercises) ? workout.data.exercises : [];
  const currentByCatalogId = new Map(currentExercises.map(exercise => [key(exercise?.exercise_id), exercise]));
  const annotations = {};

  const add = (embeddedExercise, annotation) => {
    if (!embeddedExercise?._id) return;
    const id = String(embeddedExercise._id);
    if (!annotations[id]) annotations[id] = [];
    annotations[id].push(annotation);
  };

  for (const proposal of proposals) {
    if (!WORKOUT_KINDS.has(proposal?.kind) || proposal?.status !== 'pending') continue;
    const affectedCount = affectedProposalCount(proposal);
    for (const change of proposal?.proposed_changes || []) {
      if (text(change?.entry_id) !== entryId) continue;
      const removals = Array.isArray(change?.removed_exercises) ? change.removed_exercises : [];
      const replacementIds = new Set(removals.map(removal => key(removal?.replacement_exercise_id)).filter(Boolean));
      for (const exercise of change?.exercises || []) {
        if (exercise?.proposal_status !== 'adjusted' || replacementIds.has(key(exercise?.exercise_id))) continue;
        const current = currentByCatalogId.get(key(exercise?.exercise_id));
        if (!current) continue;
        add(current, {
          proposalId: String(proposal._id || proposal.id),
          type: 'prescription',
          exerciseName: exercise.name || current.name || exercise.exercise_id,
          currentPrescription: formatCurrentPrescription(current),
          proposedPrescription: formatProposedPrescription(exercise),
          affectedCount,
          canApprove: proposal.can_approve !== false,
          scope: proposal.scope,
          reason: proposal.reason,
          appliesToFutureWorkouts: workout.status === 'ongoing'
        });
      }
      for (const removal of removals) {
        const current = currentByCatalogId.get(key(removal?.exercise_id));
        if (!current) continue;
        add(current, {
          proposalId: String(proposal._id || proposal.id),
          type: removal.replacement_exercise_id ? 'replacement' : 'removal',
          exerciseName: removal.name || current.name || removal.exercise_id,
          replacementExerciseId: removal.replacement_exercise_id || null,
          replacementName: removal.replacement_name || null,
          affectedCount,
          canApprove: proposal.can_approve !== false,
          scope: proposal.scope,
          reason: removal.reason || proposal.reason,
          appliesToFutureWorkouts: workout.status === 'ongoing'
        });
      }
    }
  }
  return annotations;
}
